import { Marker, Popup, Tooltip } from 'react-leaflet'
import L from 'leaflet'
import Box from '@mui/material/Box'
import Divider from '@mui/material/Divider'
import { useRef } from 'react'
import { Trans, useTranslation } from 'react-i18next'

// Custom Marker component that handles both waypoints and locations
export default function CustomMarker({ position, label, preview, description, location, showName, index, iconUrl, isUserLocation, tooltipOffset: propTooltipOffset, concatenatedLabel }) {
  const { t } = useTranslation('components')
  const markerRef = useRef(null)

  // Handle user location marker first (highest priority)
  if (isUserLocation && position) {
    const markerIconUrl = iconUrl || 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-blue.png'

    console.log('DEBUG: User location marker - iconUrl prop:', iconUrl)
    console.log('DEBUG: User location marker - final markerIconUrl:', markerIconUrl)

    // Create custom icon for user location
    const customIcon = new L.Icon({
      iconUrl: markerIconUrl,
      iconRetinaUrl: markerIconUrl,
      shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
      iconSize: [25, 41],
      iconAnchor: [12, 41],
      popupAnchor: [1, -34],
      shadowSize: [41, 41]
    })

    console.log('DEBUG: User location marker - customIcon created:', customIcon)

    return (
      <Marker
        position={position}
        icon={customIcon}
      >
        <Popup>
          <Box sx={{ maxWidth: 200 }}>
            <h3 style={{ margin: '8px 0', color: '#424e9c' }}>{t('map.yourLocation')}</h3>
            <div style={{ fontSize: '14px', marginBottom: '8px' }}>
              <div><Trans i18nKey='map.latitude' components={{ 1: <strong /> }} values={{ value: position[0].toFixed(6) }} /></div>
              <div><Trans i18nKey='map.longitude' components={{ 1: <strong /> }} values={{ value: position[1].toFixed(6) }} /></div>
            </div>
          </Box>
        </Popup>
      </Marker>
    )
  }

  // Handle waypoints with label, preview, or description
  if (label || preview || description) {
    return (
      <Marker position={position}>
        <Popup>
          <Box sx={{ maxWidth: 200 }}>
            {label && <h3>{label}</h3>}
            {preview && <img src={preview} alt={label} style={{ maxWidth: '100%' }} />}
            {description && <p>{description}</p>}

          </Box>
        </Popup>
      </Marker>
    )
  }

  // Handle location markers (more complex case)
  if (location && location.gpxLocation && location.gpxLocation.lat && location.gpxLocation.lng) {
    const lat = parseFloat(location.gpxLocation.lat)
    const lng = parseFloat(location.gpxLocation.lng)
    const markerIconUrl = iconUrl || location.iconUrl || 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png'

    // Create custom icon
    const customIcon = new L.Icon({
      iconUrl: markerIconUrl,
      iconRetinaUrl: markerIconUrl,
      shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
      iconSize: [25, 41],
      iconAnchor: [12, 41],
      popupAnchor: [1, -34],
      shadowSize: [41, 41]
    })

    // Format the label based on cp value
    const formattedLabel = location.cp ?
      (location.cp.toLowerCase() === 'start' || location.cp.toLowerCase() === 'finish') ?
        location.cp :
        `CP${location.cp}: ${location.name}` :
      location.name || ''

    // Use concatenated label if provided (for overlapping markers)
    const popupLabel = concatenatedLabel || formattedLabel

    // Use the provided tooltip offset or default
    const tooltipOffset = propTooltipOffset || [0, -25]

    return (
      <Marker
        key={`location-${index}`}
        position={[lat, lng]}
        icon={customIcon}
        ref={markerRef}
      >
        <Popup>
          <Box sx={{ maxWidth: 200 }}>
            <h3 style={{ margin: '8px 0', color: '#424e9c' }}>{popupLabel}</h3>
            <div style={{ fontSize: '14px', marginBottom: '8px' }}>
              {location.interDistKm && <div><Trans i18nKey='map.popup.interDist' components={{ 1: <strong /> }} values={{ km: location.interDistKm }} /></div>}
              {location.commDistKm && <div><Trans i18nKey='map.popup.commDist' components={{ 1: <strong /> }} values={{ km: location.commDistKm }} /></div>}
              {location.commDPlusM && <div><Trans i18nKey='map.popup.dPlus' components={{ 1: <strong /> }} values={{ value: location.commDPlusM }} /></div>}
              {location.commDMinusM && <div><Trans i18nKey='map.popup.dMinus' components={{ 1: <strong /> }} values={{ value: location.commDMinusM }} /></div>}
              {location.altitudeM && <div><Trans i18nKey='map.popup.altitude' components={{ 1: <strong /> }} values={{ value: location.altitudeM }} /></div>}
              <Divider sx={{ my: 0.3, borderColor: 'silver' }} />
              {location.crewSupport && <div style={{ color: '#68429c' }}><Trans i18nKey='map.popup.crewSupport' components={{ 1: <strong /> }} values={{ value: location.crewSupport }} /></div>}
              {location.services && <div style={{ color: '#68429c' }}><Trans i18nKey='map.popup.services' components={{ 1: <strong /> }} values={{ value: location.services }} /></div>}
              <Divider sx={{ my: 0.3, borderColor: 'silver' }} />
              {location.location && <div><Trans i18nKey='map.popup.location' components={{ 1: <strong /> }} values={{ value: location.location }} /></div>}
              {location.cutOffTime && <div style={{ color: 'orangered' }}><Trans i18nKey='map.popup.cutOffTime' components={{ 1: <strong /> }} values={{ value: location.cutOffTime }} /></div>}
            </div>
          </Box>
        </Popup>
        {showName && (
          <Tooltip permanent direction='top' offset={tooltipOffset} className='location-label-tooltip'>
            {formattedLabel}
          </Tooltip>
        )}
      </Marker>
    )
  }

  // Fallback for simple position-only markers
  if (position) {
    return (
      <Marker position={position} />
    )
  }

  // Fallback for locations without valid gpxLocation
  if (location && location.name) {
    console.warn(`Location "${location.name}" has no valid GPX coordinates`)
    return null
  }

  // Final fallback
  return null
}